/*
 * RefundRequestsPage.tsx (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * @file RefundRequestsPage.tsx
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useDispatch, useSelector } from 'react-redux';
import {
  IPageEvent,
  ISortEvent,
  ITablePayload
} from '@abstract/abstractwebcommon-shared/interfaces/pagination';
import { defaultTableLimit } from '@abstract/abstractwebcommon-client/Constants';
import {
  getRefundRequestsAction,
  approveRefundRequestAction,
  rejectRefundRequestAction
} from '../../../Store/RefundRequests';
import RefundRequestsTable from './RefundRequestsTable';

const RefundRequestsPage = () => {
  const dispatch = useDispatch();
  const refundRequests = useSelector((state) => state.refundRequests);
  const isLoading = useSelector(
    (state) => state.refundRequests.isFetchingList
  );

  const [tablePayload, setPayload] = useState<ITablePayload>({
    limit: defaultTableLimit,
    skip: 0,
    sort: { sortField: 'createdAt', sortOrder: -1 },
    searchTerm: ''
  }); /**< Default Payload */

  /// Fetch refund requests with the given payload.
  const fetchRefundRequests = (payload: ITablePayload): void => {
    dispatch(
      getRefundRequestsAction(
        payload.skip,
        payload.limit,
        payload.searchTerm,
        payload.sort
      )
    );
  };

  const handlePageUpdate = (event: IPageEvent): void => {
    const updatedPayload: ITablePayload = {
      skip: event.first,
      limit: event.rows,
      sort: {
        sortField: tablePayload.sort.sortField,
        sortOrder: tablePayload.sort.sortOrder
      },
      searchTerm: tablePayload.searchTerm
    };
    setPayload(updatedPayload);
    fetchRefundRequests(updatedPayload);
  };

  /// Handle sort update event
  const handleSortUpdate = (event: ISortEvent): void => {
    const updatedPayload: ITablePayload = { ...tablePayload, sort: event };
    setPayload(updatedPayload);
    fetchRefundRequests(updatedPayload);
  };

  /// Handle filter update event
  const handleFilterUpdate = (event: string): void => {
    const updatedPayload: ITablePayload = {
      ...tablePayload,
      searchTerm: event
    };
    setPayload(updatedPayload);
    fetchRefundRequests(updatedPayload);
  };

  /// Approve a refund request and refresh the queue.
  const approveRefundRequest = (refundRequestID: string): void => {
    dispatch(approveRefundRequestAction(refundRequestID, tablePayload));
  };

  /// Reject a refund request and refresh the queue.
  const rejectRefundRequest = (
    refundRequestID: string,
    rejectionReason: string
  ): void => {
    dispatch(
      rejectRefundRequestAction(refundRequestID, rejectionReason, tablePayload)
    );
  };

  useEffect(() => {
    fetchRefundRequests(tablePayload);
  }, []);

  return (
    <Row>
      <Col sm={12}>
        <RefundRequestsTable
          refundRequests={refundRequests.list}
          isLoading={isLoading}
          isReviewing={refundRequests.isReviewing}
          tablePayload={tablePayload}
          totalRecords={refundRequests.totalRecords}
          handlePageUpdate={handlePageUpdate}
          handleSortUpdate={handleSortUpdate}
          handleFilterUpdate={handleFilterUpdate}
          approveRefundRequest={approveRefundRequest}
          rejectRefundRequest={rejectRefundRequest}
        />
      </Col>
    </Row>
  );
};

export default RefundRequestsPage;
